import { createContext, useContext } from "react";
import { Switch } from "../../../../../../CardsPages/Forms/Switch";

interface IConfigSeletorContextProps<TConfig, TReturn = number | boolean> {
    configObj: {
        [Property in keyof TConfig]: TReturn;
    };
    config: Extract<keyof TConfig, string>;
    getter?: (config: TReturn) => number | boolean;
    setter?: (config: TReturn, value: number | boolean) => void;
    update?: () => void;
}
interface IConfigGeralContainerProps<TConfig, TReturn = number | boolean> {
    configObj: {
        [Property in keyof TConfig]: TReturn;
    };
    getter?: (config: TReturn) => number | boolean;
    setter?: (config: TReturn, value: number | boolean) => void;
    update?: () => void;
    children: React.ReactNode;
}
interface IConfigGeralSelectorProps<TConfig> {
    children: React.ReactNode;
    config: Extract<keyof TConfig, string>;
    title: string;
}
interface IConfigGeralOptionProps {
    value: number;
    title: string;
    description: string;
}

interface IConfigMeta<T extends object> {
    title: string;
    configs: Array<{
        config: Extract<keyof T, string>;
        title: string;
        options?: Array<{
            value: number;
            title: string;
            description?: string;
        }>;
        switch?: boolean;
    }>;
}

interface IConfigFlagsProps<T extends object> {
    configMeta: Array<IConfigMeta<T>>;
}

const ConfigGeralSelectorContext = createContext<
    IConfigSeletorContextProps<any, any>
>({
    configObj: {},
    config: "",
    update: () => {},
});

function ConfigGeralContainer<T extends object, TValue = number | boolean>(
    props: IConfigGeralContainerProps<T, TValue>
) {
    return (
        <ConfigGeralSelectorContext.Provider
            value={{
                config: "",
                configObj: props.configObj,
                update: props.update,
                getter: props.getter,
                setter: props.setter,
            }}
        >
            {props.children}
        </ConfigGeralSelectorContext.Provider>
    );
}

function ConfigGeralSelector<T extends object>(
    props: IConfigGeralSelectorProps<T>
) {
    return (
        <ConfigGeralSelectorContext.Consumer>
            {(configContext) => (
                <ConfigGeralSelectorContext.Provider
                    value={{
                        ...configContext,
                        config: props.config,
                    }}
                >
                    <div className="form-group">
                        <label>{props.title}</label>
                        <div className="row">{props.children}</div>
                    </div>
                </ConfigGeralSelectorContext.Provider>
            )}
        </ConfigGeralSelectorContext.Consumer>
    );
}

function ConfigGeralOption(props: IConfigGeralOptionProps) {
    const { config, configObj, update, getter, setter } = useContext(
        ConfigGeralSelectorContext
    );

    const { value, title, description } = props;
    const currentValue = getter ? getter(configObj[config]) : configObj[config];
    const checked = currentValue === value || currentValue === !!value;

    return (
        <div className="col-lg-4 my-1">
            <label
                className={`option ${checked && "active"} h-100`}
                role={"button"}
            >
                <span className="option-control">
                    <span className="radio">
                        <input
                            type="radio"
                            name={config}
                            value={value}
                            onChange={() => {
                                if (setter) setter(configObj[config], value);
                                else configObj[config] = value;
                                update?.();
                            }}
                            checked={checked}
                        />
                        <span></span>
                    </span>
                </span>
                <span className="option-label">
                    <span className="option-head">
                        <span className="option-title">{title}</span>
                    </span>
                    <span className="option-body text-secondary">
                        {description}
                    </span>
                </span>
            </label>
        </div>
    );
}

function ConfigFlags<TConfig extends object>(
    props: IConfigFlagsProps<TConfig>
) {
    const { configObj, update, getter, setter } = useContext(
        ConfigGeralSelectorContext
    );

    return (
        <>
            {props.configMeta.map((group) => (
                <div key={group.title} className="mb-10">
                    <h4>{group.title}</h4>
                    {group.configs.map((c) => {
                        const currentValue = getter
                            ? getter(configObj[c.config])
                            : configObj[c.config];
                        return c.options ? (
                            <ConfigGeralSelector<TConfig>
                                config={c.config}
                                title={c.title}
                                key={c.config}
                            >
                                {c.options.map((o) => (
                                    <ConfigGeralOption
                                        value={o.value}
                                        title={o.title}
                                        description={o.description ?? ""}
                                        key={o.value}
                                    />
                                ))}
                            </ConfigGeralSelector>
                        ) : (
                            <div className="form-group" key={c.config}>
                                <label className="col-12 col-form-label px-0">
                                    {c.title}
                                </label>
                                <div className="col-12">
                                    <Switch
                                        color="success"
                                        type="checkbox"
                                        defaultChecked={!!currentValue}
                                        onChange={() => {
                                            if (setter)
                                                setter(
                                                    configObj[c.config],
                                                    !currentValue
                                                );
                                            else
                                                configObj[c.config] =
                                                    !configObj[c.config];
                                            update?.();
                                        }}
                                        removeDefaultStyle
                                    />
                                </div>
                            </div>
                        );
                    })}
                </div>
            ))}
        </>
    );
}

const ConfigGeral = {
    Container: ConfigGeralContainer,
    Selector: ConfigGeralSelector,
    Option: ConfigGeralOption,
    Flags: ConfigFlags,
};
export default ConfigGeral;
export type { IConfigMeta };
